// pages/AdminDashboardPage.tsx
import { useAdminDashboard } from "@cabukaka/hooks";
import { Package, Users, Store, ShoppingCart, TrendingUp, Star, Truck, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageLayout from "../../layouts/PageListLayout";

export default function AdminDashboardPage() {
  const { data, loading, error } = useAdminDashboard();
  const navigate = useNavigate();

  if (loading) {
    return (
      <PageLayout title="Tableau de bord">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="Tableau de bord">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
          Erreur : {error}
        </div>
      </PageLayout>
    );
  }

  if (!data) return null;

  const statsCards = [
    { label: "Utilisateurs", value: data.totalUsers, icon: Users, color: "bg-blue-500" },
    { label: "Boutiques", value: data.totalShops, icon: Store, color: "bg-orange-500" },
    { label: "Clients", value: data.totalClients, icon: Users, color: "bg-green-500" },
    { label: "Produits", value: data.totalProducts, icon: Package, color: "bg-purple-500" },
    { label: "Commandes", value: data.totalOrders, icon: ShoppingCart, color: "bg-indigo-500" },
    { label: "En attente", value: data.pendingOrders, icon: Truck, color: "bg-yellow-500" },
    { label: "Livrées", value: data.completedOrders, icon: CheckCircle, color: "bg-emerald-500" },
    { label: "Chiffre d'affaires", value: `${data.totalRevenue.toLocaleString('fr-FR')} FCFA`, icon: TrendingUp, color: "bg-teal-500" },
  ];

  return (
    <PageLayout title="Tableau de bord Administrateur">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg ${stat.color} bg-opacity-10 flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${stat.color.replace('bg-', 'text-')}`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* Produits populaires */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-primary" />
            Produits les plus vendus
          </h3>
          <div className="space-y-3">
            {data.topProducts.map((product) => (
              <div
                key={product.id}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                onClick={() => navigate(`/admin/products/details?id=${product.id}`)}
              >
                <img src={product.mainImage} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{product.name}</p>
                  <p className="text-sm text-gray-500">{product.soldCount} vendus</p>
                </div>
                <p className="font-semibold text-primary">{product.priceLabel}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Dernières commandes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-primary" />
            Dernières commandes
          </h3>
          <div className="space-y-3">
            {data.recentOrders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                onClick={() => navigate(`/admin/orders/details?id=${order.id}`)}
              >
                <div>
                  <p className="font-medium text-gray-800">{order.reference}</p>
                  <p className="text-sm text-gray-500">{order.client?.name}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{order.total.toLocaleString('fr-FR')} FCFA</p>
                  <p className="text-xs text-gray-400">{new Date(order.createdAt).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Graphique mensuel simplifié */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution mensuelle</h3>
        <div className="flex items-end gap-2 h-48">
          {data.monthlyOrders.map((month, index) => (
            <div key={index} className="flex-1 flex flex-col items-center gap-2">
              <div
                className="w-full bg-primary/20 rounded-t-lg transition-all hover:bg-primary/40"
                style={{ height: `${(month.count / Math.max(...data.monthlyOrders.map(m => m.count))) * 100}%` }}
              />
              <span className="text-xs text-gray-500 rotate-45 origin-left">
                {month._id.month}/{month._id.year}
              </span>
            </div>
          ))}
        </div>
      </div>
    </PageLayout>
  );
}